import g from '#core/global';
import load from '#core/load';


const state = window.teal_state = window.teal_state || {};
state.layout = state.layout || {};
state.layout.breakpoints = state.layout.breakpoints || {};
state.layout.breakpoint_keys = state.layout.breakpoint_keys || [];
state.layout.breakpoint_values = state.layout.breakpoint_values || [];

const container_query_$ = '[data-cq]';
const root_$ = ':root';
const breakpoint_detector_$ = '.breakpoint-detector';
const breakpoint_detector_attr = 'data-breakpoint-key';
const range_modifier = 0.02;
const body_scroll_attr = 'data-allow-scroll';

let is_complete_layout = false;


const module = {
	$root: null,
	$breakpoint_detector: null,
	change_e: 'layout-change',
	palette_mode_storage_key: 'pattern_lab_palette_mode',
	palette_mode_attr: 'data-palette-mode',
	
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('layout')) return false;
		
		load.content(() => {
			is_complete_layout = assign_elements();
			if (!is_complete_layout) return false;
			
			set_breakpoints();
			setup_palette_mode();
		});
		load.all(() => {
			is_complete_layout = assign_elements();
			if (!is_complete_layout) return false;
			
			set_breakpoints();
			// this.jiggle();
		});
	},
	
	
	/**
	 * Jiggle container query heights
	 * This solves a potential problem of container-query heights not being
	 * calculated on load, resulting in unscaled elements
	 */
	jiggle() {
		const $container_queries = document.querySelectorAll(container_query_$);
		$container_queries.forEach($container => {
			const fixed_height = $container.style.height;
			const height = this.getHeight($container);
			this.setHeight($container, height + 1);
			$container.style.height = fixed_height || '';
		});
	},
	
	
	/**
	 * Get all siblings of element
	 * @param {element} $element
	 * @return {array}
	 */
	getAllSiblings($element) {
		const $children = [...$element.parentNode.children];
		return $children.filter($child => $child !== $element);
	},
	
	
	/**
	 * Get sibling of element by selector
	 * @param {element} $element
	 * @param {string} selector
	 * @return {element}
	 */
	getSibling($element, selector) {
		if (!util.isValidSelector(selector)) return null;
		
		const $children = [...$element.parentNode.children];
		return $children.find($child => $child.matches(selector));
	},
	
	
	/**
	 * Get dimensional property of element
	 * @param {element} $element
	 * @param {string} property
	 * @param {boolean} with_margin
	 * @return {number}
	 */
	getProperty($element, property, with_margin = true) {
		const styles = getComputedStyle($element);
		const dimension = parseFloat(styles[property]);
		if (isNaN(dimension)) return 0;
		if (!with_margin) return dimension;
		
		const margin_properties = (property === 'width')
			? ['marginLeft', 'marginRight']
			: ['marginTop', 'marginBottom']
		;
		const margin_total = parseFloat(styles[margin_properties[0]])
			+ parseFloat(styles[margin_properties[1]]);
		return dimension + margin_total;
	},
	
	
	/**
	 * Set dimensional property of element
	 * @param {element} $element
	 * @param {string} property
	 * @param {number} value
	 */
	setProperty($element, property, value) {
		if (typeof value === 'function') {
			value = value();
		}
		$element.style[property] = (typeof value !== 'string')
			? value + 'px'
			: value;
	},
	
	
	/**
	 * Get width of element
	 * @param {element} $element
	 * @param {boolean} with_margin
	 * @return {number}
	 */
	getWidth($element, with_margin = true) {
		return this.getProperty($element, 'width', with_margin);
	},
	
	
	/**
	 * Get height of element
	 * @param {element} $element
	 * @param {boolean} with_margin
	 * @return {number}
	 */
	getHeight($element, with_margin = true) {
		return this.getProperty($element, 'height', with_margin);
	},
	
	
	/**
	 * Set width of element
	 * @param {element} $element
	 * @param {number} value
	 */
	setWidth($element, value) {
		this.setProperty($element, 'width', value);
	},
	
	
	/**
	 * Set height of element
	 * @param {element} $element
	 * @param {number} value
	 */
	setHeight($element, value) {
		this.setProperty($element, 'height', value);
	},
	
	
	/**
	 * Get lower and upper bounds for current breakpoint
	 * @param {int} width
	 * @return {object}
	 */
	getBreakpointBounds(width) {
		if (width < 0) return 0;
		
		set_breakpoints_order();
		const breakpoint_bounds = {
			min: null,
			max: null,
		};
		
		for (const breakpoint_value of state.layout.breakpoint_values) {
			const index = state.layout.breakpoint_values.indexOf(breakpoint_value);
			breakpoint_bounds.max = breakpoint_value - range_modifier;
			if (width < breakpoint_value) break;
			
			breakpoint_bounds.min = breakpoint_value;
			if (index >= state.layout.breakpoint_values.length - range_modifier) {
				breakpoint_bounds.max = null;
			}
		}
		
		return breakpoint_bounds;
	},
	
	
	/**
	 * Get current breakpoint name
	 * @param {int} width
	 * @return {string}
	 */
	getBreakpointName(width) {
		if (width < 0) return 0;
		
		set_breakpoints_order();
		
		let breakpoint_name = '';
		state.layout.breakpoint_values.forEach((breakpoint_value, index) => {
			if (width >= breakpoint_value) {
				breakpoint_name = state.layout.breakpoint_keys[index];
			}
		});
		return breakpoint_name;
	},
	
	
	/**
	 * Is the viewport at least as wide as the given breakpoint?
	 * @param {string} breakpoint
	 * @return {boolean}
	 */
	isBreakpointMin(breakpoint) {
		if (!state.layout.breakpoints[breakpoint]) return false;
		
		const breakpoint_width = state.layout.breakpoints[breakpoint];
		return window.innerWidth >= breakpoint_width;
	},
	
	
	/**
	 * Is the viewport narrower than the given breakpoint?
	 * @param {string} breakpoint
	 * @return {boolean}
	 */
	isBreakpointMax(breakpoint) {
		if (!state.layout.breakpoints[breakpoint]) return false;
		
		const window_breakpoint = this.getBreakpointName(window.innerWidth);
		const window_breakpoint_width = state.layout.breakpoints[window_breakpoint];
		const breakpoint_width = state.layout.breakpoints[breakpoint];
		return window_breakpoint_width < breakpoint_width;
	},
	
	
	/**
	 * Check if element is intersecting with the scannable area
	 * 
	 * Lookaround scales region to be scanned based on viewport height:
	 * - 0   = Only pass objects that are touching the vertical center of the
	 *         viewport
	 * - 0.5 = Expand scanned area to the middle 50% of the viewport height
	 * - 1.0 = Only pass objects that are visible in the viewport (even a single
	 *         pixel row)
	 * - 1.5 = Look outside of viewport by an additional 50% of viewport height
	 * 
	 * Diagram of lookaround values:
	 *              ... = Higher values extend range further
	 *              1.5 = Look outside viewport for object in proximity
	 * ·———————·    1.0 = Any part of object must be within viewport
	 * |       |    0.5 = Object must be within middle section of viewport
	 * |- - - -| <- 0   = Object must be touching middle of viewport
	 * |       |    0.5 = Object must be within middle section of viewport
	 * ·———————·    1.0 = Any part of object must be within viewport
	 *              1.5 = Look outside viewport for object in proximity
	 *              ... = Higher values extend range further
	 * 
	 * @param {element} $element
	 * @param {float} lookaround
	 * @param {boolean} top_only
	 * @param {boolean} bottom_only
	 * @return {boolean}
	 */
	isIntersecting({$element = null, lookaround = 0, top_only = false, bottom_only = false} = {}) {
		if ($element.offsetParent === null) return false;
		
		const bounds = $element.getBoundingClientRect();
		const window_height = window.innerHeight;
		
		const lookaround_top = (window_height * (0.5 - (lookaround / 2)));
		const lookaround_bottom = (window_height * (0.5 + (lookaround / 2)));
		const is_top_above_lookaround = bounds.top < lookaround_bottom;
		const is_bottom_below_lookaround = bounds.bottom > lookaround_top;
		
		if (top_only) return is_top_above_lookaround;
		if (bottom_only) return is_bottom_below_lookaround;
		
		return (
			is_top_above_lookaround
			&& is_bottom_below_lookaround
		);
	},
	
	
	/**
	 * Toggle scrolling
	 * @param {boolean} allow
	 */
	allowScroll(allow) {
		if (allow) {
			document.body.removeAttribute(body_scroll_attr);
			return true;
		}
		
		document.body.setAttribute(body_scroll_attr, g.false_s);
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (is_complete_layout) return true;
	
	module.$root = document.querySelector(root_$);
	if (!module.$root) return false;
	
	module.$breakpoint_detector = document.querySelector(breakpoint_detector_$);
	return true;
}


/**
 * Set breakpoints from HTML
 */
function set_breakpoints() {
	if (!module.$breakpoint_detector) return false;
	if (Object.keys(state.layout.breakpoints).length) return true;
	
	const $breakpoints = [...module.$breakpoint_detector.children];
	$breakpoints.forEach($breakpoint => {
		const name = $breakpoint.getAttribute(breakpoint_detector_attr);
		const width = module.getWidth($breakpoint);
		state.layout.breakpoints[name] = width;
	});
}


/**
 * Create ordered arrays of breakpoint keys and values
 */
function set_breakpoints_order() {
	if (
		state.layout.breakpoint_keys.length
		&& state.layout.breakpoint_values.length
	) return true;
	
	set_breakpoints();
	const breakpoint_keys = Object.keys(state.layout.breakpoints).sort((a, b) => {
		if (state.layout.breakpoints[a] < state.layout.breakpoints[b]) return -1;
		if (state.layout.breakpoints[a] > state.layout.breakpoints[b]) return 1;
		return 0;
	});
	const breakpoint_values = Object.values(state.layout.breakpoints)
		.map(value => parseInt(value))
		.sort((a, b) => {
			if (a < b) return -1;
			if (a > b) return 1;
			return 0;
		})
	;
	state.layout.breakpoint_keys = breakpoint_keys;
	state.layout.breakpoint_values = breakpoint_values;
}


/**
 * Setup palette mode toggle
 */
function setup_palette_mode() {
	const $toggle = document.querySelector('.palette-mode-toggle');
	if (!$toggle) return false;
	
	set_palette_mode(get_palette_mode(true));
	
	$toggle.addEventListener(g.click_e, e => {
		const mode = (get_palette_mode() === 'light')
			? 'dark'
			: 'light';
		set_palette_mode(mode);
	});
}


/**
 * Get palette mode
 * @param {boolean} use_local_storage
 * @return {string}
 */
function get_palette_mode(use_local_storage = false) {
	const $root = document.documentElement;
	const mode = (use_local_storage)
		? localStorage.getItem(module.palette_mode_storage_key)
		: $root.getAttribute(module.palette_mode_attr);
	return mode || 'light';
}


/**
 * Set palette mode
 * @param {string} mode
 */
function set_palette_mode(mode) {
	const $root = document.documentElement;
	$root.setAttribute(module.palette_mode_attr, mode);
	localStorage.setItem(module.palette_mode_storage_key, mode);
}
